import * as THREE from 'three';

/* ============================================================
   從既有貼圖算法線圖／粗糙度圖
   ============================================================

   使用者 2026-08-04（質感這一輪）選的方案：**法線 ＋ 粗糙度貼圖**，
   理由是這個專案的貼圖**全部是 canvas 程序生成的** —— 高度資訊本來就在
   亮度裡（裂縫是暗的、碎石是亮的、窗框是暗的），所以法線可以直接算出來，
   不必畫第二張圖、也不增加任何 draw call。

   ## 兩個一定要做對的地方

   1. **法線圖／粗糙度圖是「資料」不是「顏色」，encoding 必須是 Linear。**
      本專案的 `mkt()`／`tex()` 一律設 `sRGBEncoding`（那對色彩貼圖是對的），
      照抄到法線圖上會讓每一個分量被 gamma 扭一次 —— 表面看起來「有凹凸但
      方向怪怪的」，而且不會有任何錯誤訊息。
   2. **Sobel 的取樣要繞回（wrap）**，因為這些貼圖都是 `RepeatWrapping`。
      邊界夾住的話，每一塊 52 m 的接縫上會出現一條亮線 —— 在空曠場地上
      那會變成整齊的格線，正好是使用者 2026-08-03 要求拿掉的東西。

   ## 成本

   一次性。整套（地面 1024² ＋ 建築 7 張 512²）約 260 萬像素的 Sobel，
   實測在開場建圖時一次做完，不進每幀路徑。headless 沒有 canvas，
   所有函式先問 `typeof document`，拿不到就回 null。 */

/** 從一張 `CanvasTexture` 的亮度算法線圖。
    `strength` 是凹凸的深淺（1 ≈ 混凝土的顆粒，2 以上會開始讀成浮雕）。 */
export function normalFrom(tex: any, strength: number) {
  if (!tex || typeof document === 'undefined') return null;
  var src = tex.image;
  if (!src || !src.width) return null;
  var W = src.width, H = src.height;
  var sc: any = document.createElement('canvas'); sc.width = W; sc.height = H;
  var sg = sc.getContext('2d'); sg.drawImage(src, 0, 0);
  var sd = sg.getImageData(0, 0, W, H).data;

  /* 亮度先攤成一張 Float 高度場。逐點在 `sd` 上取樣的話，每個像素要做
     四次 `(y*W+x)*4` 的乘加 —— 1024² 下那是四百萬次多餘的算術。 */
  var hmap = new Float32Array(W * H);
  for (var i = 0, p = 0; i < hmap.length; i++, p += 4) {
    hmap[i] = (sd[p] * 0.299 + sd[p + 1] * 0.587 + sd[p + 2] * 0.114) / 255;
  }

  var out: any = document.createElement('canvas'); out.width = W; out.height = H;
  var og = out.getContext('2d');
  var od = og.createImageData(W, H), o = od.data;
  for (var y = 0; y < H; y++) {
    /* 見檔頭第 2 點：上下左右都要繞回。 */
    var y0 = ((y - 1) + H) % H, y1 = (y + 1) % H;
    for (var x = 0; x < W; x++) {
      var x0 = ((x - 1) + W) % W, x1 = (x + 1) % W;
      var dx = (hmap[y * W + x1] - hmap[y * W + x0]) * strength;
      var dy = (hmap[y1 * W + x] - hmap[y0 * W + x]) * strength;
      /* 切線空間法線：(-dx, -dy, 1) 正規化後映到 0–255。
         Z 固定 1 —— 高度場的斜率就是 XY，不需要真的算高度。 */
      var len = Math.sqrt(dx * dx + dy * dy + 1);
      var k = (y * W + x) * 4;
      o[k] = ((-dx / len) * 0.5 + 0.5) * 255;
      o[k + 1] = ((-dy / len) * 0.5 + 0.5) * 255;
      o[k + 2] = ((1 / len) * 0.5 + 0.5) * 255;
      o[k + 3] = 255;
    }
  }
  og.putImageData(od, 0, 0);
  var t = new THREE.CanvasTexture(out);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  /* ⚠️ **不要設 sRGBEncoding**（見檔頭第 1 點）。r128 的預設就是 Linear，
     所以這裡是「刻意不設」，不是忘了設 —— 別的貼圖函式都有設。 */
  t.anisotropy = 4;
  return t;
}

/** 從亮度算粗糙度圖：**亮的地方粗、暗的地方滑**。
    這個對應在廢墟上成立的理由是「暗＝積了水或油、亮＝乾的粉塵與碎石」，
    而那正是濕地反射要的遮罩（見 `render/ground.ts` 的 `wetTex`）。
    `lo`／`hi` 是粗糙度的下上限 —— 全域 0–1 會讓乾的地方變成純漫射的紙。 */
export function roughFrom(tex: any, lo: number, hi: number) {
  if (!tex || typeof document === 'undefined') return null;
  var src = tex.image;
  if (!src || !src.width) return null;
  var W = src.width, H = src.height;
  var sc: any = document.createElement('canvas'); sc.width = W; sc.height = H;
  var sg = sc.getContext('2d'); sg.drawImage(src, 0, 0);
  var img = sg.getImageData(0, 0, W, H), d = img.data;
  for (var p = 0; p < d.length; p += 4) {
    var lum = (d[p] * 0.299 + d[p + 1] * 0.587 + d[p + 2] * 0.114) / 255;
    /* three 讀的是**綠通道**當粗糙度（金屬度讀藍）。三個通道一起寫是為了
       讓這張圖在除錯時肉眼看得懂（灰階），成本一樣。 */
    var v = (lo + (hi - lo) * lum) * 255;
    d[p] = d[p + 1] = d[p + 2] = v;
  }
  var out: any = document.createElement('canvas'); out.width = W; out.height = H;
  out.getContext('2d').putImageData(img, 0, 0);
  var t = new THREE.CanvasTexture(out);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;                 // 同樣是資料圖：不設 sRGBEncoding
  return t;
}
